import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import AIReadinessPanel from '../../components/AIReadinessPanel';

interface Page {
  id: string;
  title: string;
  url: string;
  html: string;
  optimizedHtml?: string;
  createdAt: string;
  analysis?: {
    seoScore?: number;
    geoScore?: number;
  };
}

const EditPage: React.FC = () => {
  console.log('🚀 ENHANCED IFRAME EDITOR LOADED - Timestamp:', new Date().toISOString());
  
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeSrc, setIframeSrc] = useState<string>('');
  const [isLoadingIframe, setIsLoadingIframe] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [autoAnalysisStatus, setAutoAnalysisStatus] = useState<string>('');
  const [showAIReadiness, setShowAIReadiness] = useState(false);
  const [aiReadinessScore, setAiReadinessScore] = useState<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (pageId) {
      fetchPage();
    }
  }, [pageId]);

  // Auto-load enhanced iframe when page data is available
  useEffect(() => {
    if (page && !iframeSrc && !isLoadingIframe) {
      createIframeFromSavedHtml();
    }
  }, [page]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      console.log('Fetching page with ID:', pageId);
      const response = await api.get(`/pages/${pageId}`);
      const pageData = response.data.page;
      console.log('Page data received:', pageData);
      console.log('HTML content length:', pageData.html?.length || 0);
      console.log('Page URL:', pageData.url);
      setPage(pageData);
    } catch (err) {
      console.error('Error fetching page:', err);
      setError('Failed to load page data');
      toast.error('Failed to load page data');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard/pages');
  };

  const processHtmlWithBaseUrl = (html: string, baseUrl: string): string => {
    if (!baseUrl || !html) return html;

    // Create a temporary div to process the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Add base tag if it doesn't exist
    if (!tempDiv.querySelector('base')) {
      const baseTag = document.createElement('base');
      baseTag.setAttribute('href', baseUrl.endsWith('/') ? baseUrl : baseUrl + '/');
      tempDiv.insertBefore(baseTag, tempDiv.firstChild);
    }

    // Fix relative image URLs
    const images = tempDiv.querySelectorAll('img');
    images.forEach(img => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('http') && !src.startsWith('data:') && !src.startsWith('/')) {
        img.setAttribute('src', baseUrl + '/' + src);
      } else if (src && src.startsWith('/')) {
        img.setAttribute('src', baseUrl + src);
      }
    });

    // Fix relative link URLs
    const links = tempDiv.querySelectorAll('a[href]');
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href && !href.startsWith('http') && !href.startsWith('mailto:') && !href.startsWith('#') && !href.startsWith('/')) {
        link.setAttribute('href', baseUrl + '/' + href);
      } else if (href && href.startsWith('/')) {
        link.setAttribute('href', baseUrl + href);
      }
    });

    // Fix relative CSS background images
    const elementsWithBackground = tempDiv.querySelectorAll('[style*="background"]');
    elementsWithBackground.forEach(el => {
      const style = el.getAttribute('style');
      if (style && style.includes('url(')) {
        const newStyle = style.replace(/url\(['"]?([^'"]+)['"]?\)/g, (match, url) => {
          if (url && !url.startsWith('http') && !url.startsWith('data:') && !url.startsWith('/')) {
            return `url('${baseUrl}/${url}')`;
          } else if (url && url.startsWith('/')) {
            return `url('${baseUrl}${url}')`;
          }
          return match;
        });
        el.setAttribute('style', newStyle);
      }
    });

    return tempDiv.innerHTML;
  };


  const extractBaseUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return `${urlObj.protocol}//${urlObj.host}`;
    } catch {
      return '';
    }
  };

  const createIframeFromSavedHtml = (pageData?: Page) => {
    const currentPage = pageData || page;
    if (!currentPage) return;
    
    setIsLoadingIframe(true);
    setIframeError(null);

    try {
      // Get the saved HTML content (optimizedHtml or fallback to html)
      const htmlContent = currentPage.optimizedHtml || currentPage.html;
      
      if (!htmlContent || htmlContent.length === 0) {
        throw new Error('No saved HTML content available for this page');
      }

      console.log('Creating iframe from saved HTML content, length:', htmlContent.length);
      console.log('Page URL for base URL:', currentPage.url);

      // Extract base URL from the original page URL
      const baseUrl = extractBaseUrl(currentPage.url);
      
      // Process the saved HTML content with base URL
      let processedHtml = htmlContent;
      
      // Add base URL to the HTML head if it doesn't exist
      if (!processedHtml.includes('<base') && baseUrl) {
        const baseTag = `<base href="${baseUrl}/">`;
        processedHtml = processedHtml.replace('<head>', `<head>${baseTag}`);
      }

      // Fix relative image URLs to absolute URLs if baseUrl is available
      if (baseUrl) {
        // Fix relative URLs that start with /
        processedHtml = processedHtml.replace(
          /(src|href)=["']\/([^"']+)["']/g,
          `$1="${baseUrl}/$2"`
        );

        // Fix relative URLs that don't start with /
        processedHtml = processedHtml.replace(
          /(src|href)=["'](?!https?:\/\/|data:|#)([^"']+)["']/g,
          `$1="${baseUrl}/$2"`
        );
      }

      // Clean up old blob URL if it exists
      if (iframeSrc && iframeSrc.startsWith('blob:')) {
        URL.revokeObjectURL(iframeSrc);
      }

      // Create a blob URL instead of data URL to avoid iframe restrictions
      const blob = new Blob([processedHtml], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      setIframeSrc(blobUrl);
      setIsLoadingIframe(false);

      console.log('Successfully created iframe from saved HTML content');

    } catch (err) {
      console.error('Error creating iframe from saved HTML:', err);
      setIframeError(`Failed to create iframe from saved content: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoadingIframe(false);
    }
  };

  const fetchAndCreateEnhancedIframe = async () => {
    if (!page?.url) return;
    
    setIsLoadingIframe(true);
    setIframeError(null);

    try {
      // Try multiple CORS proxies to get the HTML content
      const proxies = [
        `https://api.allorigins.win/get?url=${encodeURIComponent(page.url)}`,
        `https://cors-anywhere.herokuapp.com/${page.url}`,
        `https://thingproxy.freeboard.io/fetch/${page.url}`,
        `https://api.codetabs.com/v1/proxy?quest=${page.url}`,
        `https://corsproxy.io/?${encodeURIComponent(page.url)}`,
        `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(page.url)}`,
        `https://yacdn.org/proxy/${page.url}`,
        `https://cors.bridged.cc/${page.url}`,
        `https://proxy.cors.sh/${page.url}`,
        `https://api.allorigins.win/raw?url=${encodeURIComponent(page.url)}`
      ];

      let content = null;
      let workingProxy = '';

      for (const proxy of proxies) {
        try {
          console.log(`Trying proxy: ${proxy}`);
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const response = await fetch(proxy, {
            method: 'GET',
            headers: {
              'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
              'Accept-Language': 'en-US,en;q=0.5',
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
            },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);

          if (response.ok) {
            let data;
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
              data = await response.json();
              // Handle different JSON response formats
              content = data.contents || data.content || data.data || data;
            } else {
              // Handle plain text/HTML responses
              content = await response.text();
            }
            
            // Validate that we got actual HTML content
            if (content && typeof content === 'string' && content.includes('<html')) {
              workingProxy = proxy;
              console.log(`Success with proxy: ${proxy}`);
              break;
            } else {
              console.log(`Proxy returned invalid content: ${proxy}`);
              continue;
            }
          }
        } catch (err) {
          console.log(`Proxy failed: ${proxy}`, err);
          continue;
        }
      }

      if (!content) {
        throw new Error(`All ${proxies.length} CORS proxies failed. This website may have strict security measures.`);
      }

      // Clean and process the content
      let cleanedContent = content
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
        .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
        .replace(/on\w+='[^']*'/gi, '') // Remove event handlers
        .replace(/javascript:/gi, '') // Remove javascript: protocols
        .replace(/data-src/gi, 'src') // Convert data-src to src for images
        .replace(/srcset="[^"]*"/gi, '') // Remove srcset attributes
        .replace(/sizes="[^"]*"/gi, '') // Remove sizes attributes
        .replace(/<!--[\s\S]*?-->/g, ''); // Remove HTML comments

      // Extract base URL from the original URL
      const baseUrl = extractBaseUrl(page.url);
      
      // Add base URL to the HTML head if it doesn't exist
      if (!cleanedContent.includes('<base')) {
        const baseTag = `<base href="${baseUrl}/">`;
        cleanedContent = cleanedContent.replace('<head>', `<head>${baseTag}`);
      }

      // Fix relative image URLs to absolute URLs
      cleanedContent = cleanedContent.replace(
        /(src|href)=["']\/([^"']+)["']/g,
        `$1="${baseUrl}/$2"`
      );

      // Fix relative URLs that don't start with /
      cleanedContent = cleanedContent.replace(
        /(src|href)=["'](?!https?:\/\/|data:|#)([^"']+)["']/g,
        `$1="${baseUrl}/$2"`
      );

      // Create a blob URL instead of data URL to avoid iframe restrictions
      const blob = new Blob([cleanedContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      setIframeSrc(blobUrl);
      setIsLoadingIframe(false);

    } catch (err) {
      console.error('Error fetching website content:', err);
      setIframeError(`Failed to fetch website content: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoadingIframe(false);
    }
  };

  const enableEditing = () => {
    if (!iframeRef.current) return;

    try {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (!iframeDoc) {
        setIframeError('Cannot access iframe content for editing');
        return;
      }

      // Make the content editable
      iframeDoc.body.contentEditable = 'true';
      iframeDoc.body.style.cursor = 'text';
      
      // Add some visual indicators for editing
      iframeDoc.body.style.outline = '2px solid #007bff';
      iframeDoc.body.style.outlineOffset = '10px';
      
      // Add editing styles
      const style = iframeDoc.createElement('style');
      style.textContent = `
        * {
          cursor: text !important;
        }
        *:hover {
          outline: 1px dashed #007bff !important;
        }
        [contenteditable="true"] {
          outline: 2px solid #007bff !important;
          outline-offset: 2px !important;
        }
        .editing-hint {
          position: fixed;
          top: 10px;
          right: 10px;
          background: #007bff;
          color: white;
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
          z-index: 9999;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
      `;
      iframeDoc.head.appendChild(style);

      // Add editing hint
      const hint = iframeDoc.createElement('div');
      hint.className = 'editing-hint';
      hint.textContent = '✏️ Click any text to edit';
      iframeDoc.body.appendChild(hint);
      
      setIsEditing(true);
      console.log('Editing enabled in iframe');
    } catch (err) {
      console.error('Error enabling editing:', err);
      setIframeError('Cannot enable editing due to iframe restrictions');
    }
  };

  const disableEditing = () => {
    if (!iframeRef.current) return;

    try {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (iframeDoc) {
        iframeDoc.body.contentEditable = 'false';
        iframeDoc.body.style.cursor = 'default';
        iframeDoc.body.style.outline = 'none';
        
        // Remove editing hint
        const hint = iframeDoc.querySelector('.editing-hint');
        if (hint) {
          hint.remove();
        }
      }
      
      setIsEditing(false);
    } catch (err) {
      console.error('Error disabling editing:', err);
    }
  };

  const saveContent = async () => {
    if (!iframeRef.current) return;

    try {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (iframeDoc) {
        const html = iframeDoc.documentElement.outerHTML;
        await handleEnhancedIframeSave(html);
      } else {
        setIframeError('Cannot save content due to iframe restrictions');
      }
    } catch (err) {
      console.error('Error saving content:', err);
      setIframeError('Cannot save content due to iframe restrictions');
    }
  };

  const handleEnhancedIframeSave = async (content: string) => {
    try {
      const baseUrl = extractBaseUrl(page?.url || '');
      const processedHtml = processHtmlWithBaseUrl(content, baseUrl);
      
      // Create updated page data
      const updatedPage = page ? { ...page, optimizedHtml: processedHtml } : null;
      
      // Update the page state with the processed HTML
      setPage(updatedPage);
      
      // Show AI analysis status
      setAutoAnalysisStatus('🤖 AI Agent is analyzing changes and updating common elements across all pages...');
      
      // Save to backend (this will trigger automatic AI analysis)
      const response = await api.put(`/pages/${page?.id}`, {
        optimizedHtml: processedHtml
      });
      
      // Check if AI analysis was performed
      if (response.data.aiAnalysis?.performed) {
        setAutoAnalysisStatus('✅ AI Agent automatically updated common elements across all pages!');
        toast.success('Page saved! AI Agent automatically updated common elements across all pages.');
        
        // Clear status after 5 seconds
        setTimeout(() => {
          setAutoAnalysisStatus('');
        }, 5000);
      } else {
        setAutoAnalysisStatus('');
        toast.success('Page saved successfully!');
      }
      
      // Refresh the iframe with the updated content
      if (updatedPage) {
        createIframeFromSavedHtml(updatedPage);
      }
    } catch (error) {
      console.error('Save error:', error);
      setAutoAnalysisStatus('');
      toast.error('Failed to save page');
    }
  };

  // AI Readiness Analysis function
  const analyzeAIReadiness = async () => {
    if (!page) return;
    
    try {
      setIsAnalyzing(true);
      const response = await api.get(`/ai-readiness/analyze/${page.id}`);
      
      if (response.data.analysis.success) {
        setAiReadinessScore(response.data.analysis.score);
        setShowAIReadiness(true);
        toast.success(`AI Readiness Score: ${response.data.analysis.score}/100`);
      } else {
        toast.error('Failed to analyze AI readiness');
      }
    } catch (error) {
      console.error('AI Readiness Analysis Error:', error);
      toast.error('Failed to analyze AI readiness');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // AI Analysis functions
  const analyzePageForCommonElements = async () => {
    if (!page) return;
    
    setIsAnalyzing(true);
    try {
      const response = await api.get('/ai-agent/analyze');
      if (response.data.success) {
        setAiSuggestions(response.data.analysis || {});
        setShowAiPanel(true);
        toast.success('AI analysis complete! Found common elements.');
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      toast.error('AI analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const updateCommonElement = async (elementType: string, newContent: string) => {
    try {
      const response = await api.post('/ai-agent/update-element', {
        elementType,
        newContent,
        pageIds: [page?.id]
      });
      
      if (response.data.success) {
        toast.success(`${elementType} updated successfully!`);
        // Refresh the iframe with updated content
        createIframeFromSavedHtml();
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update element');
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (iframeSrc && iframeSrc.startsWith('blob:')) {
        URL.revokeObjectURL(iframeSrc);
      }
    };
  }, [iframeSrc]);

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading page data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center">
            <h5 className="text-danger">Error</h5>
            <p className="text-muted">{error || 'Page not found'}</p>
            <button className="btn btn-primary" onClick={handleBack}>
              Back to Pages
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (iframeSrc) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div className="p-3 border-bottom bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-globe me-2"></i>
              Page Editor - {page.url}
            </h6>
            <div className="d-flex gap-2">
              {!isEditing ? (
                <button className="btn btn-sm btn-primary" onClick={enableEditing}>
                  <i className="fas fa-edit me-1"></i>
                  Enable Editing
                </button>
              ) : (
                <button className="btn btn-sm btn-secondary" onClick={disableEditing}>
                  <i className="fas fa-eye me-1"></i>
                  Disable Editing
                </button>
              )}
              <button 
                className="btn btn-sm btn-warning" 
                onClick={analyzeAIReadiness}
                disabled={isAnalyzing}
              >
                <i className="fas fa-chart-line me-1"></i>
                {isAnalyzing ? 'Analyzing...' : 'AI Readiness'}
                {aiReadinessScore !== null && (
                  <span className={`badge ms-1 ${aiReadinessScore >= 70 ? 'bg-success' : aiReadinessScore >= 50 ? 'bg-warning' : 'bg-danger'}`}>
                    {aiReadinessScore}
                  </span>
                )}
              </button>
              <button 
                className="btn btn-sm btn-info" 
                onClick={analyzePageForCommonElements}
                disabled={isAnalyzing}
              >
                <i className="fas fa-robot me-1"></i>
                {isAnalyzing ? 'AI Analyzing...' : 'AI Analysis'}
              </button>
              <button className="btn btn-sm btn-success" onClick={saveContent}>
                <i className="fas fa-save me-1"></i>
                Save
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={handleBack}>
                <i className="fas fa-arrow-left me-1"></i>
                Back to Pages
              </button>
            </div>
          </div>
        </div>

        {/* Iframe */}
        <div style={{ flex: 1, position: 'relative' }}>
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: 'white'
            }}
            title="Enhanced Website Editor"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
          />
        </div>

        {/* AI Panel */}
        {showAiPanel && (
          <div className="p-3 border-top bg-light">
            <div className="alert alert-info mb-3">
              <h6 className="mb-2">
                <i className="fas fa-robot me-2"></i>
                AI Analysis Results
              </h6>
              <p className="mb-0">AI found common elements that can be updated across all your pages.</p>
            </div>
            
            {aiSuggestions && Object.keys(aiSuggestions).length > 0 && (
              <div className="row">
                {Object.entries(aiSuggestions).map(([elementType, elements]: [string, any]) => (
                  <div key={elementType} className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0 text-capitalize">
                          {elementType} ({Array.isArray(elements) ? elements.length : 0} found)
                        </h6>
                      </div>
                      <div className="card-body">
                        {Array.isArray(elements) && elements.length > 0 && (
                          <div>
                            <textarea
                              className="form-control mb-2"
                              rows={4}
                              defaultValue={elements[0].content?.substring(0, 200) + '...'}
                              onChange={(e) => {
                                // Store the updated content for this element type
                                setAiSuggestions(prev => ({
                                  ...prev,
                                  [elementType]: [{ ...elements[0], content: e.target.value }]
                                }));
                              }}
                            />
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                const newContent = (aiSuggestions as any)[elementType][0].content;
                                updateCommonElement(elementType, newContent);
                              }}
                            >
                              <i className="fas fa-sync me-1"></i>
                              Update {elementType}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="d-flex justify-content-between">
              <button 
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowAiPanel(false)}
              >
                <i className="fas fa-times me-1"></i>
                Close AI Panel
              </button>
              <button 
                className="btn btn-sm btn-info"
                onClick={analyzePageForCommonElements}
                disabled={isAnalyzing}
              >
                <i className="fas fa-refresh me-1"></i>
                Re-analyze
              </button>
            </div>
          </div>
        )}

        {/* Auto Analysis Status */}
        {autoAnalysisStatus && (
          <div className="p-2 border-top bg-info text-white">
            <div className="alert alert-info mb-0 py-2" role="alert">
              <small>
                <i className="fas fa-robot me-1"></i>
                <strong>AI Agent Status:</strong> {autoAnalysisStatus}
              </small>
            </div>
          </div>
        )}

        {/* Info Panel */}
        <div className="p-2 border-top bg-light">
          <div className={`alert ${isEditing ? 'alert-warning' : 'alert-success'} mb-0 py-2`} role="alert">
            <small>
              <i className={`fas ${isEditing ? 'fa-edit' : 'fa-check-circle'} me-1`}></i>
              <strong>{isEditing ? 'Editing Mode Active:' : 'Saved Content View:'}</strong> 
              {isEditing 
                ? ' Click on any text or element to edit it directly. Changes will be saved to the database when you click Save. AI Agent will automatically update common elements across all pages.'
                : ' Displaying saved HTML content with base URL for proper image loading. Click "Enable Editing" to start editing.'
              }
            </small>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingIframe) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5>Loading Saved Page Content...</h5>
            <p className="text-muted">{page.url}</p>
            <small className="text-muted">Processing saved HTML with base URL for images...</small>
          </div>
        </div>
      </div>
    );
  }

  if (iframeError) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center" style={{ maxWidth: '600px', padding: '20px' }}>
            <div className="text-danger mb-3">
              <i className="fas fa-exclamation-triangle fa-3x"></i>
            </div>
            <h5>Error Loading Saved Content</h5>
            <p className="text-muted mb-4">{iframeError}</p>
            
            <div className="d-flex gap-2 justify-content-center flex-wrap">
              <button className="btn btn-primary" onClick={() => createIframeFromSavedHtml()}>
                <i className="fas fa-redo me-1"></i>
                Try Again
              </button>
              <button className="btn btn-outline-secondary" onClick={handleBack}>
                <i className="fas fa-arrow-left me-1"></i>
                Back to Pages
              </button>
            </div>
          </div>
        </div>

        {/* AI Readiness Panel */}
        {showAIReadiness && (
          <div className="ai-readiness-overlay">
            <div className="ai-readiness-container">
              <AIReadinessPanel
                pageId={page?.id || ''}
                pageTitle={page?.title || ''}
                pageUrl={page?.url || ''}
                onClose={() => setShowAIReadiness(false)}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // This should not be reached as iframeSrc will be set when page loads
  return null;
};

export default EditPage;